import type { OktaAccessToken } from "../../auth/models/DxoUserModel";
import { APPLICATION_ENVIRONMENT } from "../../shared/const/environment";
import type {
  AdminAgentReasoningEffort,
  AdminAgentUsage,
  AdminAgentSqlReviewQuery,
} from "../models/admin-agent.models";
import { ADMIN_AGENT_SQL_REVIEW_TOKENS } from "./admin-agent.constants";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

export const formatAdminAgentReasoningEffort = (effort: AdminAgentReasoningEffort): string => {
  if (effort === "default") return "Default (Medium)";
  if (effort === "xhigh") return "Extra high";

  return effort.charAt(0).toUpperCase() + effort.slice(1);
};

export const formatAdminAgentCost = (usage: AdminAgentUsage): string => {
  if (usage.modelCalls > 0 && usage.unpricedCalls === usage.modelCalls) {
    return "Cost unavailable";
  }

  const amount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(usage.estimatedCostUsd);

  return `${amount} est.${usage.unpricedCalls ? " (partial)" : ""}`;
};

export const createAdminAgentRequestId = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

export const getAdminAgentErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { message?: unknown; error?: unknown } | string | undefined;

    if (typeof body === "string" && body.trim()) {
      return body;
    }

    if (body && typeof body === "object") {
      const message = body.message ?? body.error;

      if (typeof message === "string") {
        return message;
      }
    }

    return error.response?.status === 403
      ? "This assistant is available to OpsLink administrators only."
      : "Could not reach the admin assistant. Check the connection and try again.";
  }

  return error instanceof Error ? error.message : "The admin assistant request failed.";
};

export const formatAdminAgentTime = (value: string): string => {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Compare identity claims, allowing renewal of the same session without reusing a previous account's bearer.
// This is a browser isolation check; the backend and gateway remain responsible for authentication.
export const getAdminAgentBrowserTokenScope = (rawToken: string, requireActive = false): string => {
  const token = jwtDecode<OktaAccessToken>(rawToken);

  if (
    !token ||
    typeof token.sub !== "string" ||
    !token.sub ||
    !Array.isArray(token.groups) ||
    !token.groups.every((group) => typeof group === "string")
  ) {
    throw new Error("The current sign-in is unavailable. Sign in again.");
  }

  if (
    requireActive &&
    (typeof token.exp !== "number" || !Number.isFinite(token.exp) || token.exp * 1000 <= Date.now())
  ) {
    throw new Error("The current sign-in expired. Sign in again.");
  }

  return JSON.stringify([
    APPLICATION_ENVIRONMENT,
    token.sub,
    [...token.groups].sort(),
    token.firstname,
    token.lastname,
  ]);
};

export const readAdminAgentSqlReviewQueries = (value: unknown): AdminAgentSqlReviewQuery[] => {
  if (!Array.isArray(value)) return [];

  return value.map((entry: unknown, index) => {
    const query = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {};

    return {
      id: typeof query.id === "string" ? query.id : `Q${index + 1}`,
      purpose: typeof query.purpose === "string" ? query.purpose : "",
      binds: Array.isArray(query.binds) ? query.binds : [],
    };
  });
};

// Show the reviewed statement with its actual values; execution keeps using the original SQL and typed binds.
export const formatAdminAgentReviewSql = (sql: string, binds: unknown[]): string => {
  let bindIndex = 0;

  return sql
    .replace(ADMIN_AGENT_SQL_REVIEW_TOKENS, (token: string, clause: string | undefined) => {
      if (token === "?" && bindIndex < binds.length) {
        const value = binds[bindIndex++];
        if (value === null) return "NULL";
        if (typeof value === "string") return `'${value.replace(/\\/g, "\\\\").replace(/'/g, "''")}'`;
        if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
        return String(value);
      }

      return clause ? `\n${clause.toUpperCase().replace(/\s+/g, " ")}` : token;
    })
    .trim();
};
